/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  effect,
  Injector,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, skip } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  SidebarComponent,
  NebulaTitleSlot,
  NebulaNavigationService,
  NebulaToastService,
  MenuItem,
  BreadcrumbItem,
} from 'nebula-ui-kit';
import { SidebarService } from '../../../../data-access/src/lib/sidebar.service';
import { SidebarStateService } from '../../../../data-access/src/lib/sidebar-state.service';
import { SidebarMenuItem } from '../../../../domain/src/lib/sidebar.model';
import { BreadcrumbSyncService } from '../../../../data-access/src/lib/breadcrumb-sync.service';
import { isRouteValid } from '../../../../utils/src/lib/route-validator';
import { detectLayoutModule, isAtOpenTabLimit } from '../../../../utils/src/lib/tab-limit-util';
import { ModuleKey, RouteSyncService } from '../../../../utils/src/lib/route-sync';
import { MODULOS_EN_CONSTRUCCION } from '../../../../const/src/lib/modules.constant';

/** Alternativa de regex con los modulos en construccion (`mass-billing|supplies|...`). */
const EN_CONSTRUCCION = MODULOS_EN_CONSTRUCCION.join('|');
import {
  FavoritosService,
  LAYOUT,
  MenuIconService,
  TranslationService,
  UiProfileService,
  leerContextoDeProducto,
} from '@nebula-shared/data-access';

/**
 * Etiquetas por segmento de ruta que uso este sidebar antes de i18n.
 *
 * @deprecated Ya no se consulta: el breadcrumb resuelve por
 * `SidebarService.labelForSegment`. Se conserva como registro de las
 * etiquetas de entonces; las nuevas van al bundle, no aqui.
 */
export const SIDEBAR_ROUTE_SEGMENT_LABELS: Record<string, string> = {
  accounting: 'Contabilidad',
  tesorería: 'Tesorería',
  budget: 'Presupuesto',
  masters: 'Maestros',
  'cost-center': 'Centros de Costo',
  'cost-center-type': 'Tipos de Centro de Costo',
  list: 'Listado',
  create: 'Crear',
  edit: 'Editar',
  detail: 'Detalle',
  view: 'Ver',
  dashboard: 'Inicio',
  ingest: 'Carga de Documentos',
  preservation: 'Preservacion Digital',
  retention: 'Retencion Documental',
  'legal-hold': 'Custodia Legal ',
  reports: 'Reportes Generales',
  cloud: 'Respaldo Externo',
  notifications: 'Notificaciones',
  audit: 'Historial de Actividad',
  settings: 'Configuracion General',
  admin: 'Administrador',
  tenants: 'Gestion de Empresas',
  permissions: 'Permisos',
  servers: 'Configuracion Servidores',
  'preservation-messages': 'Mensajes Informativos',
  'repository-admin': 'Boveda Digital',
  'accounting-parameters': 'Parámetros',
  'tipo-plan': 'Tipos de Plan',
  'tipo-plan-historico': 'Histórico de Tipos de Plan',
  dependency: 'Dependencias',
  'dian-concept': 'Conceptos Dian',
  'account-structure': 'Estructura de Cuentas',
  currency: 'Monedas',
  'account-plan': 'Plan Cuentas',
  'third-party': 'Terceros',
  'accounting-format': 'Formatos Contables',
  accrual: 'Causación Contable',
  'deferred-process': 'Proceso de Diferidos',
  'journal-entry': 'Asiento Contable',
  'accrual-approval/list': 'Aprobar Causación',
  'accrual-annulment/list': 'Anular Causación',
  'treasury-parameters': 'Parámetros',
  depreciation: 'Depreciación',
  'journal-entry-annulment': 'Anulación Asientos',
  'bank-account': 'Cuentas Bancarias',
  'other-discount': 'Otros Descuentos',
  'other-deductions': 'Otras Deducciones',
  deferred: 'Diferidos',
  'journal-entry-deferred': 'Diferidos',
  'journal-classification': 'Clasificaciones',
  'accounting-concepts': 'Conceptos',
  'journal-entry-type': 'Tipo Asientos',
  'accounting-currency': 'Monedas',
  'exchange-rate': 'Tasas de Cambio',
  'treasury-third-party': 'Terceros',
  'payment-annulment': 'Anulación Pagos',
  'bank-payment': 'Pagos Bancarios',
  'expense-voucher': 'Comprobante Egreso',
  'income-voucher': 'Comprobante Ingreso',
  'income-annulment': 'Anulación Ingresos',
  invoice: 'Factura',
  parameters: 'Parámetros de Facturación',
  'concepts-revenue': 'Revenue Concepts',
  'payment-file-scructure': 'Escructura de Pago',
  'payment-files': 'Archivos de Pago',
  'processing-of-bank': 'Traslados Bancarios',
  'processing-of-bank-cancellation': 'Anulación Traslados Bancarios',
  'files-scructure-configurations': 'Configuración de Archivos',
  'bank-transfer': 'Traslados Bancarios',
  'bank-transfer-annulment': 'Anulación Traslados Bancarios',
  'bank-closure': 'Cierre Bancario',
};

@Component({
  selector: 'app-sidebar-wrapper',
  standalone: true,
  imports: [CommonModule, SidebarComponent, NebulaTitleSlot, MatTooltipModule],
  template: `
    <lib-sidebar
      [menuItems]="menuVisible()"
      [collapsed]="collapsed()"
      [isLoading]="isLoading"
      [logoUrl]="logoUrl()"
      [logoAlt]="logoAlt()"
      (itemClick)="onItemClick($event)"
      (accionClick)="alPulsarAccion($event)"
      (collapsedChange)="onToggleSidebar()"
      [title]="sidebarTitle()"
    >
      @if (productoEnCabecera(); as producto) {
        <!-- Perfil 3.0: la cabecera muestra el producto activo en lugar del nombre de la aplicacion. -->
        <div
          title
          class="producto-activo"
          [matTooltip]="producto.descripcion"
          [matTooltipDisabled]="!producto.descripcion"
          matTooltipPosition="right"
          matTooltipClass="lib-custom-tooltip"
        >
          <span class="producto-activo__textos">
            <span class="producto-activo__rotulo">{{ producto.rotulo }}</span>
            <span class="producto-activo__nombre">{{ producto.nombre }}</span>
          </span>
        </div>
      }
    </lib-sidebar>
  `,
  styles: `
    /* La cabecera del kit tiene alto fijo y una sola linea: con el producto, el nombre se ve
       completo y baja de renglon si es largo, como los nombres de las opciones del perfil 3.0. */
    :host ::ng-deep .sidebar-header:has(.producto-activo) {
      height: auto;
      min-height: 64px;
      white-space: normal;
    }

    .producto-activo {
      display: flex;
      align-items: center;
      min-width: 0;
    }

    .producto-activo__textos {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    .producto-activo__rotulo {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      opacity: 0.72;
    }

    .producto-activo__nombre {
      margin-top: 1px;
      font-size: 18px;
      font-weight: 700;
      line-height: 1.2;
      letter-spacing: -0.2px;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarWrapperComponent implements OnInit, OnDestroy {
  readonly collapsed = input<boolean>(false);
  readonly collapsedChange = output<boolean>();
  readonly routeSelected = output<string>();
  readonly logoUrl = input<string | undefined>();
  readonly logoAlt = input<string>('Nebula App');

  readonly sidebarService = inject(SidebarService);
  private readonly translation = inject(TranslationService);
  readonly router = inject(Router);
  readonly navService = inject(NebulaNavigationService);
  readonly sidebarState = inject(SidebarStateService);
  readonly cdr = inject(ChangeDetectorRef);
  readonly injector = inject(Injector);
  readonly breadcrumbSync = inject(BreadcrumbSyncService);

  readonly destroy$ = new Subject<void>();
  private currentProductId: string | null = null;
  private reloadTimeout: any = null;
  private initialLoadDone = false;

  /** Los avisos del menu salen en la zona de toast del kit (Mantis #212). */
  private readonly toast = inject(NebulaToastService);
  readonly routeSync = inject(RouteSyncService);
  private readonly menuIconService = inject(MenuIconService);

  readonly sidebarTitle = computed(() => {
    const currentModule = leerContextoDeProducto('selectedRoute') || 'accounting';

    const specificTitles: Record<string, string> = {
      vault: 'Nebula Vault',
      invoicing: 'Facturación',
    };
    return specificTitles[currentModule] || 'Nebula ERP';
  });
  isLoading = false;
  menuItems: SidebarMenuItem[] = [];

  /**
   * Lo que la cabecera muestra del producto activo, ya en el idioma vigente: solo en el perfil
   * 3.0 y solo con el producto resuelto. Nulo en otro caso, y la cabecera sigue con su titulo.
   */
  readonly productoEnCabecera = computed(() => {
    const producto = this.sidebarService.productoActivo();
    this.translation.lang();
    if (!producto || !this.perfilVisual.esPerfil30()) return null;
    return {
      rotulo: this.translation.translate('SELECTOR.STEP_MODULE'),
      nombre: this.traducirLiteral(producto.nombre),
      descripcion: this.traducirLiteral(producto.descripcion),
    };
  });

  /**
   * Traduce un texto que puede venir como clave; si no hay traduccion, queda el texto tal cual.
   *
   * @param literal nombre o descripcion tal como llega del menu.
   * @returns el texto a mostrar.
   */
  private traducirLiteral(literal: string): string {
    if (!literal) return literal;
    const valor = this.translation.translate(literal);
    return valor && valor !== literal ? valor : literal;
  }

  private readonly favoritos = inject(FavoritosService);
  private readonly perfilVisual = inject(UiProfileService);

  /** Accion de las opciones del grupo Favoritos. */
  private static readonly QUITAR_FAVORITO = 'quitar-favorito';

  /**
   * Accion pulsada en una opcion del menu: la de Favoritos la quita, sin abrir la pantalla.
   *
   * @param evento la opcion y su accion.
   */
  alPulsarAccion(evento: { item: { route?: string | null }; accion: string }): void {
    if (evento.accion === SidebarWrapperComponent.QUITAR_FAVORITO) {
      this.favoritos.quitar(evento.item.route);
    }
  }

  /** El menu tal como se cargo; `menuItems` lo sigue leyendo el resto (migas, iconos). */
  private readonly menuCargado = signal<SidebarMenuItem[]>([]);

  /**
   * Lo que pinta el sidebar: el menu cargado y, arriba, el grupo Favoritos con las opciones que
   * el usuario marco, en el orden en que las marco. Solo con el perfil 3.0; sin favoritos, el
   * grupo no aparece.
   */
  readonly menuVisible = computed<SidebarMenuItem[]>(() => {
    const menu = this.menuCargado();
    const claves = this.favoritos.claves();
    if (!claves.length || !this.perfilVisual.esPerfil30()) return menu;

    const hojas = new Map<string, SidebarMenuItem>();
    const recorrer = (items: SidebarMenuItem[]): void => {
      for (const item of items) {
        if (item.children?.length) recorrer(item.children);
        else if (item.route) hojas.set(this.favoritos.claveDe(item.route), item);
      }
    };
    recorrer(menu);

    // Cada favorito se quita desde el propio menu, sin abrir la pantalla.
    const quitar = {
      id: SidebarWrapperComponent.QUITAR_FAVORITO,
      icono: 'StarOff' as const,
      etiqueta: this.translation.translate(LAYOUT.FAVORITES.REMOVE),
    };
    const opciones = claves
      .map((clave) => hojas.get(clave))
      .filter((hoja): hoja is SidebarMenuItem => !!hoja)
      .map((hoja) => ({ ...hoja, accion: quitar }));
    if (!opciones.length) return menu;

    const grupo: SidebarMenuItem = {
      label: this.translation.translate(LAYOUT.FAVORITES.TITLE),
      icon: 'Star',
      children: opciones,
      expanded: true,
    };
    return [grupo, ...menu];
  });

  ngOnInit(): void {
    // Los favoritos son del usuario que acaba de entrar.
    this.favoritos.recargar();
    this.loadMenu();
    this.listenToProductChanges();
    this.listenToRouteChanges();
    this.listenToLanguageChanges();
  }

  /** Recarga el menu cuando cambia el idioma para re-traducir las etiquetas (omite el valor inicial). */
  private listenToLanguageChanges(): void {
    this.translation.currentLang$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => {
      this.loadMenu();
    });
  }

  listenToProductChanges(): void {
    effect(
      () => {
        const productId = leerContextoDeProducto('selectedProductId');
        if (productId !== this.currentProductId) {
          this.currentProductId = productId;
          if (this.initialLoadDone) {
            this.reloadMenuWithDelay();
          }
        }
      },
      { injector: this.injector },
    );
  }

  listenToRouteChanges(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.sincronizarIconoConLaRuta(event.urlAfterRedirects || event.url);

        const newProductId = leerContextoDeProducto('selectedProductId');
        if (newProductId !== this.currentProductId && this.initialLoadDone) {
          this.currentProductId = newProductId;
          this.reloadMenuWithDelay();
        }
      }
    });
  }

  /**
   * Deja en el encabezado el icono de la opcion QUE SE ESTA VIENDO.
   *
   * <p>El icono se fijaba solo al pulsar en el menu, asi que llegar por una pestaña, por un enlace
   * o por una recarga dejaba puesto el de la ultima opcion pulsada. Cada pantalla lo parcheaba
   * declarando el suyo, y de ahi que una misma opcion tuviera un icono en el sidebar y otro en el
   * titulo.</p>
   *
   * <p>El icono de una opcion es UNO y lo declara el menu. Resolverlo por la ruta activa es lo que
   * hace que no pueda divergir.</p>
   *
   * <p>Con la misma opcion publica su nombre, que es el contexto que muestra Nebulina, y la clave
   * del `ai_context` que su configuracion declara, que Nebulina manda a la IA.</p>
   *
   * @param url ruta activa tras la navegacion
   */
  private sincronizarIconoConLaRuta(url: string): void {
    const opcion = this.buscarOpcionPorRuta(this.menuItems, this.rutaComparable(url));
    if (opcion) {
      this.menuIconService.setIcon(opcion.icon);
    }
    this.menuIconService.setOptionName(opcion?.label ?? '');
    this.menuIconService.setOptionAiContext(
      (opcion && this.sidebarService.aiContextForRoute(opcion.route)) || '',
    );
  }

  /**
   * Deja la url en la misma forma en que el menu guarda sus rutas.
   * @param url url tal como la entrega el router
   * @returns la url sin parametros y con los acentos decodificados
   */
  private rutaComparable(url: string): string {
    const sinParametros = url.split('?')[0];
    try {
      return decodeURIComponent(sinParametros);
    } catch {
      // Una url mal formada no debe tumbar la navegacion: se compara tal cual.
      return sinParametros;
    }
  }

  /**
   * Busca, en el arbol del menu, la opcion con icono cuya ruta corresponde a la url.
   *
   * @param items rama del menu a recorrer
   * @param url   ruta activa, sin parametros de consulta
   * @returns la opcion, o null si ninguna corresponde
   */
  private buscarOpcionPorRuta(items: SidebarMenuItem[], url: string): SidebarMenuItem | null {
    let opcion: SidebarMenuItem | null = null;
    let largoDeLaCoincidencia = 0;

    for (const item of items ?? []) {
      const ruta = (item as { route?: string }).route;
      if (ruta && url.startsWith(ruta) && ruta.length > largoDeLaCoincidencia && item.icon) {
        opcion = item;
        largoDeLaCoincidencia = ruta.length;
      }
      const hijos = (item as { children?: SidebarMenuItem[] }).children;
      if (hijos?.length) {
        const enHijos = this.buscarOpcionPorRuta(hijos, url);
        if (enHijos) {
          const rutaHija = this.rutaMasLargaQueCoincide(hijos, url);
          if (rutaHija >= largoDeLaCoincidencia) {
            opcion = enHijos;
            largoDeLaCoincidencia = rutaHija;
          }
        }
      }
    }

    return opcion;
  }

  private rutaMasLargaQueCoincide(items: SidebarMenuItem[], url: string): number {
    let largo = 0;
    for (const item of items ?? []) {
      const ruta = (item as { route?: string }).route;
      if (ruta && url.startsWith(ruta) && ruta.length > largo) largo = ruta.length;
      const hijos = (item as { children?: SidebarMenuItem[] }).children;
      if (hijos?.length) largo = Math.max(largo, this.rutaMasLargaQueCoincide(hijos, url));
    }
    return largo;
  }

  reloadMenuWithDelay(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }

    this.reloadTimeout = setTimeout(() => {
      this.reloadMenu();
    }, 150);
  }

  reloadMenu(): void {
    this.menuItems = [];
    this.menuCargado.set([]);
    this.isLoading = true;
    this.cdr.markForCheck();
    this.loadMenu();
  }

  loadMenu(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.sidebarService
      .getMenu()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: SidebarMenuItem[]) => {
          this.menuItems = this.processMenuRoutesStatic(data);
          this.menuCargado.set(this.menuItems);
          this.isLoading = false;
          this.initialLoadDone = true;

          this.sincronizarIconoConLaRuta(this.router.url);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.initialLoadDone = true;
          this.cdr.markForCheck();
        },
      });
  }

  processMenuRoutesStatic(menu: SidebarMenuItem[]): SidebarMenuItem[] {
    const processItem = (item: SidebarMenuItem): SidebarMenuItem => {
      const processed = { ...item };

      if (processed.route) {
        processed.route = this.normalizeRouteStatic(processed.route);
      }

      if (processed.children?.length) {
        processed.children = processed.children.map((child) => processItem(child));
      }

      return processed;
    };

    return menu.map((item) => processItem(item));
  }

  /* eslint-disable no-useless-escape */
  normalizeRouteStatic(route: string): string {
    let normalized = route;
    try {
      normalized = decodeURIComponent(normalized);
    } catch (e) {
      console.log(e);
    }

    normalized = normalized.split('?')[0].split('#')[0];

    if (!normalized.startsWith('/')) {
      normalized = '/' + normalized;
    }

    const routeMatch = normalized.match(/^\/layout\/([^\/]+)/);
    const routeModule = routeMatch ? routeMatch[1] : null;

    const validModules = [
      'accounting',
      'tesorería',
      'tesoreria',
      'budget',
      'vault',
      'maestros',
      'invoicing',
      ...MODULOS_EN_CONSTRUCCION,
    ];
    const hasValidModule = routeModule && validModules.includes(routeModule.toLowerCase());

    // ✅ Si ya tiene /layout/ y un módulo válido, preservar la ruta completa
    if (hasValidModule) {
      normalized = normalized.replace(/\/+/g, '/');
      if (!normalized.startsWith('/')) normalized = '/' + normalized;
      return normalized;
    }

    // Si no tiene /layout/, construir la ruta completa
    const activeModule = leerContextoDeProducto('selectedRoute');
    const targetModule = validModules.includes(activeModule || '')
      ? activeModule === 'tesoreria'
        ? 'tesorería'
        : activeModule || 'accounting'
      : 'accounting';

    // Eliminar duplicados de módulos
    normalized = normalized.replace(
      new RegExp(
        `/layout/(accounting|tesorería|tesoreria|budget|vault|maestros|invoicing|${EN_CONSTRUCCION})/\\1/`,
        'g',
      ),
      '/layout/$1/',
    );
    normalized = normalized.replace(/\/+/g, '/');
    if (!normalized.startsWith('/')) normalized = '/' + normalized;

    if (
      !normalized.match(
        new RegExp(
          `^/layout/(accounting|tesorería|tesoreria|budget|vault|maestros|invoicing|${EN_CONSTRUCCION})/`,
        ),
      )
    ) {
      const cleanPath = normalized
        .replace(
          new RegExp(
            `^/(accounting|tesorería|tesoreria|budget|vault|maestros|invoicing|${EN_CONSTRUCCION})/`,
          ),
          '',
        )
        .replace(/^\//, '');
      normalized = `/layout/${targetModule}/${cleanPath}`;
    }

    return normalized;
  }
  /* eslint-enable no-useless-escape */
  detectModule(route: string): string {
    const routeLower = route.toLowerCase();

    const enConstruccion = MODULOS_EN_CONSTRUCCION.find((m) => routeLower.includes(`/${m}/`));
    if (enConstruccion) return enConstruccion;

    if (routeLower.includes('invoicing') || routeLower.includes('factura')) {
      return 'invoicing';
    }

    const budgetKeywords = [
      'budget',
      'presupuesto',
      'budget-parameters',
      'resource-type',
      'funding-source',
    ];

    const tesoreriaKeywords = [
      'tesorería',
      'tesoreria',
      'treasury',
      'bank',
      'bank-account',
      'pago',
      'payment',
      'banco',
      'resource-type',
      'embargos',
      'treasury-parameters',
    ];

    const vaultKeywords = [
      'vault',
      'dashboard',
      'ingest',
      'preservation',
      'retention',
      'legal-hold',
      'reports',
      'cloud',
      'notifications',
      'audit',
      'settings',
      'admin',
    ];

    if (budgetKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'budget';
    }

    if (tesoreriaKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'tesorería';
    }

    if (vaultKeywords.some((keyword) => routeLower.includes(keyword))) {
      return 'vault';
    }

    return 'accounting';
  }

  /** True si la ruta es la pestaña base (dashboard) del modulo, que no cuenta para el limite. */
  private _isModuleBaseRoute(route: string, module: string): boolean {
    const n = route
      .toLowerCase()
      .replace(/tesoreria/g, 'tesorería')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/$/, '');
    return n === `/${module}` || n === `/layout/${module}`;
  }

  getRouteKey(route: string | undefined | null): string {
    if (!route) return '';

    let normalized = route.toLowerCase();
    normalized = normalized.split('?')[0].split('#')[0];
    normalized = normalized.replace(/\/+/g, '/');

    normalized = normalized.replace(/tesoreria/g, 'tesorería');
    normalized = normalized.replace(/\/layout\/(tesoreria)\//g, '/layout/tesorería/');

    return normalized;
  }

  updateBreadcrumbForMenuItem(item: any, route: string, _module: string): void {
    const cleanRoute = route.split('?')[0].split('#')[0];
    const parts = cleanRoute.split('/').filter((p) => p && p !== '');

    let startIndex = parts.indexOf('layout');
    if (startIndex !== -1) startIndex++;
    else startIndex = 0;

    // Si no hay suficientes partes, usar el módulo actual
    if (startIndex >= parts.length) {
      const moduleKey = leerContextoDeProducto('selectedRoute') || 'accounting';
      const dashboardRoute = `/layout/${moduleKey}`;
      this.breadcrumbSync.update([
        {
          label: this.getLabelForRouteSegment(moduleKey),
          route: dashboardRoute,
          icon: 'Folder',
        },
      ]);
      return;
    }

    const breadcrumbItems: BreadcrumbItem[] = [];
    let accumulatedPath = '';

    const moduleName = parts[startIndex] || '';

    for (let i = startIndex; i < parts.length; i++) {
      const segment = parts[i];

      const label = this.getLabelForRouteSegment(segment);

      const pathParts = parts.slice(0, i + 1);
      accumulatedPath = '/' + pathParts.join('/');

      if (segment === moduleName && i > startIndex) {
        continue;
      }

      const lastItem = breadcrumbItems[breadcrumbItems.length - 1];
      if (
        lastItem?.label === label &&
        !this.sidebarService.conservaTrasElModulo(segment, breadcrumbItems.length)
      ) {
        continue;
      }

      const isLast = i === parts.length - 1;

      let icon = 'Folder';
      if (i === startIndex) {
        icon = 'Folder';
      } else if (isLast) {
        icon = 'File';
      } else {
        icon = 'Folder';
      }

      breadcrumbItems.push({
        label: label,
        route: isLast ? undefined : accumulatedPath,
        icon: icon,
      });
    }

    if (breadcrumbItems.length === 1) {
      const moduleLabel = this.getLabelForRouteSegment(moduleName);
      breadcrumbItems[0] = {
        ...breadcrumbItems[0],
        label: moduleLabel,
        route: `/layout/${moduleName}`,
        icon: 'Folder',
      };
    }

    this.breadcrumbSync.update(breadcrumbItems);
  }

  /**
   * Obtiene el icono de un grupo del menú por su label
   */
  private getGroupIconFromMenu(groupLabel: string): string | null {
    for (const menuItem of this.menuItems) {
      if (menuItem.label === groupLabel && menuItem.icon) {
        return menuItem.icon;
      }
      if (menuItem.children) {
        for (const child of menuItem.children) {
          if (child.label === groupLabel && child.icon) {
            return child.icon;
          }
        }
      }
    }
    return null;
  }

  getLabelForRouteSegment(segment: string): string {
    const labels: Record<string, string> = {
      accounting: 'Contabilidad',
      tesorería: 'Tesorería',
      budget: 'Presupuesto',
      masters: 'Maestros',
      'cost-center': 'Centros de Costo',
      'cost-center-type': 'Tipos de Centro de Costo',
      list: 'Listado',
      create: 'Crear',
      edit: 'Editar',
      detail: 'Detalle',
      view: 'Ver',
      dashboard: 'Inicio',
      ingest: 'Carga de Documentos',
      preservation: 'Preservacion Digital',
      retention: 'Retencion Documental',
      'legal-hold': 'Custodia Legal',
      reports: 'Reportes Generales',
      cloud: 'Respaldo Externo',
      notifications: 'Notificaciones',
      audit: 'Historial de Actividad',
      settings: 'Configuracion General',
      admin: 'Administrador',
      tenants: 'Gestion de Empresas',
      permissions: 'Permisos',
      servers: 'Configuracion Servidores',
      'preservation-messages': 'Mensajes Informativos',
      'repository-admin': 'Boveda Digital',
      'accounting-parameters': 'Parámetros', // ✅ Asegurar que esta clave existe
      'tipo-plan': 'Tipos de Plan',
      'tipo-plan-historico': 'Histórico de Tipos de Plan',
      dependency: 'Dependencias',
      'dian-concept': 'Conceptos Dian',
      'account-structure': 'Estructura de Cuentas',
      currency: 'Monedas',
      'account-plan': 'Plan Cuentas',
      'third-party': 'Terceros',
      'accounting-format': 'Formatos Contables',
      accrual: 'Causación Contable',
      'deferred-process': 'Proceso de Diferidos',
      'journal-entry': 'Asiento Contable',
      'accrual-approval/list': 'Aprobar Causación',
      'accrual-annulment/list': 'Anular Causación',
      'treasury-parameters': 'Parámetros',
      depreciation: 'Depreciación',
      'journal-entry-annulment': 'Anulación Asientos',
      'bank-account': 'Cuentas Bancarias',
      'other-discount': 'Otros Descuentos',
      'other-deductions': 'Otras Deducciones',
      deferred: 'Diferidos',
      'journal-entry-deferred': 'Diferidos',
      'journal-classification': 'Clasificaciones',
      'accounting-concepts': 'Conceptos',
      'journal-entry-type': 'Tipo Asientos',
      'accounting-currency': 'Monedas',
      'exchange-rate': 'Tasas de Cambio',
      'files-scructure-configurations/list': 'Configuración de Archivos',
      'bank-transfer': 'Traslados Bancarios',
      'bank-transfer-annulment': 'Anulación Traslados Bancarios',
      'bank-closure': 'Cierre Bancario',
    };

    if (labels[segment]) return labels[segment];

    if (/^\d+$/.test(segment) || /^[0-9a-f]{8}-/.test(segment)) {
      return 'Detalle';
    }

    return segment
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  onItemClick(item: any, domEvent?: Event): void {
    domEvent?.preventDefault();
    domEvent?.stopPropagation();

    if (Array.isArray(item.children) && item.children.length > 0) {
      if (item.icon) this.menuIconService.setIcon(item.icon);
      return;
    }

    let routeToUse = item.route;
    if (!routeToUse || typeof routeToUse !== 'string' || routeToUse.trim() === '') {
      return;
    }

    routeToUse = this.normalizeRouteStatic(routeToUse);

    const currentUrl = this.router.url;
    const currentCleanUrl = currentUrl.split('?')[0].split('#')[0];
    const modeMatch = currentCleanUrl.match(/\/(view|create|edit|detail|list)\//);

    if (modeMatch && routeToUse) {
      const currentMode = modeMatch[1];
      if (!routeToUse.includes(`/${currentMode}/`)) {
        const parts = routeToUse.split('/');
        const lastIndex = parts.length - 1;
        const lastPart = parts[lastIndex];

        if (['list', 'view', 'create', 'edit', 'detail'].includes(lastPart)) {
          parts[lastIndex] = currentMode;
        } else {
          parts.splice(lastIndex, 0, currentMode);
        }

        routeToUse = parts.join('/');
      }
    }

    const routeKey = this.getRouteKey(routeToUse);

    if (!isRouteValid(routeToUse)) {
      return;
    }

    if (item?.label) {
      const title = item.label || item.name || item.title || 'Sin título';
      const icon = item.icon || undefined;
      const detectedModule = this.detectModule(routeToUse);

      const existingTab = this.navService.tabs().find((t) => {
        if (!t.route) return false;
        return this.getRouteKey(t.route) === routeKey;
      });

      if (existingTab && existingTab.id) {
        if (icon) this.menuIconService.setIcon(icon);
        this.navService.selectTab(existingTab.id);
        this.updateBreadcrumbForMenuItem(item, routeToUse, detectedModule);
      } else {
        // El tope se cuenta con la MISMA regla que corta el alta de la pestaña
        // (installOpenTabLimit): el modulo sale del segmento /layout/<modulo>/. Con
        // `detectModule`, que busca palabras en la ruta, Conciliacion Bancaria contaba como
        // tesoreria («bank») y Configuracion de Extractos como vault («settings»): el menu creia
        // que habia sitio, no avisaba, y el alta se cortaba en silencio.
        if (isAtOpenTabLimit(this.navService.tabs(), detectLayoutModule(routeToUse))) {
          this.toast.warning(this.translation.translate('COMMON.ERROR.MAX_TABS_OPEN'));
          return;
        }

        // El icono se fija solo si la pestaña se abre de verdad (Mantis #381).
        if (icon) this.menuIconService.setIcon(icon);
        this.navService.addTabFromMenu({
          label: title,
          route: routeToUse,
          icon: icon,
        } as MenuItem);

        this.updateBreadcrumbForMenuItem(item, routeToUse, detectedModule);
      }

      this.routeSelected.emit(routeToUse);

      const moduleKey = detectedModule as ModuleKey;

      const moduleInRoute = this._extractModuleFromRoute(routeToUse);

      const finalModuleKey =
        moduleInRoute && this._isValidModule(moduleInRoute)
          ? (moduleInRoute as typeof moduleKey)
          : moduleKey;

      this.routeSync.saveRoute(finalModuleKey, routeToUse);

      this.cdr.markForCheck();
    }
  }

  _extractModuleFromRoute(route: string): string | null {
    const cleanRoute = route.split('?')[0].split('#')[0];
    const parts = cleanRoute.split('/').filter((p) => p && p !== '');

    const layoutIndex = parts.indexOf('layout');
    if (layoutIndex !== -1 && parts[layoutIndex + 1]) {
      const moduleCandidate = parts[layoutIndex + 1];
      return moduleCandidate === 'tesoreria' ? 'tesorería' : moduleCandidate;
    }

    if (parts[0] && this._isValidModule(parts[0])) {
      const moduleCandidate = parts[0];
      return moduleCandidate === 'tesoreria' ? 'tesorería' : moduleCandidate;
    }

    return null;
  }

  _isValidModule(module: string): boolean {
    return [
      'accounting',
      'tesorería',
      'tesoreria',
      'budget',
      'vault',
      'maestros',
      'invoicing',
      ...MODULOS_EN_CONSTRUCCION,
    ].includes(module);
  }

  onToggleSidebar(): void {
    const newCollapsedState = !this.collapsed();
    this.collapsedChange.emit(newCollapsedState);
    this.sidebarState.setState(!newCollapsedState, true);
  }

  ngOnDestroy(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
